import React from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from '../../components/dashboard/DashboardLayout';
import MiAfiliacionCard from '../../components/dashboard/MiAfiliacionCard';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { useAuth } from '../../contexts/AuthContext';
import { mockPayments, mockMembershipPlans } from '../../data/mock';
import {
  CreditCard,
  Calendar,
  Download,
  AlertCircle,
  CheckCircle2,
  Clock,
  FileText,
  ArrowRight,
  RefreshCw
} from 'lucide-react';

const MembresiaPage = () => {
  const { user } = useAuth();
  
  const currentPlan = mockMembershipPlans.find(p => p.id === user?.membership?.plan) || mockMembershipPlans[0];
  const daysRemaining = user?.membership?.daysRemaining || 0;
  const membershipProgress = Math.max(0, Math.min(100, ((365 - daysRemaining) / 365) * 100));

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-emerald-100 text-emerald-700';
      case 'expiring': return 'bg-amber-100 text-amber-700';
      case 'expired': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'active': return 'Activa';
      case 'expiring': return 'Por vencer';
      case 'expired': return 'Vencida';
      default: return 'Desconocido';
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Portal del afiliado — datos reales del propio expediente */}
        <MiAfiliacionCard />

        {/* Membership Status Card */}
        <Card className="overflow-hidden">
          <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 p-6 text-white">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between">
              <div>
                <div className="flex items-center space-x-3 mb-2">
                  <CreditCard className="h-6 w-6" />
                  <h2 className="text-xl font-bold">Mi Membresía</h2>
                </div>
                <p className="text-emerald-100">Plan {currentPlan.name}</p>
              </div>
              <Badge className={`mt-4 md:mt-0 ${getStatusColor(user?.membership?.status)}`}>
                {getStatusText(user?.membership?.status)}
              </Badge>
            </div>
          </div>
          <CardContent className="p-6">
            <div className="grid md:grid-cols-3 gap-6">
              <div>
                <p className="text-sm text-gray-500">Fecha de inicio</p>
                <p className="font-semibold">
                  {user?.membership?.startDate 
                    ? new Date(user.membership.startDate).toLocaleDateString('es-MX', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric'
                      })
                    : 'N/A'
                  }
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Fecha de vencimiento</p>
                <p className="font-semibold">
                  {user?.membership?.endDate 
                    ? new Date(user.membership.endDate).toLocaleDateString('es-MX', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric'
                      })
                    : 'N/A'
                  }
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Días restantes</p>
                <p className="font-semibold text-emerald-600">{daysRemaining} días</p>
              </div>
            </div>

            <div className="mt-6">
              <div className="flex justify-between text-sm mb-2">
                <span className="text-gray-500">Progreso de membresía</span>
                <span className="text-gray-700">{Math.round(membershipProgress)}% transcurrido</span>
              </div>
              <Progress value={membershipProgress} className="h-3" />
            </div>

            {daysRemaining <= 30 && (
              <div className="mt-6 p-4 bg-amber-50 rounded-lg flex items-start space-x-3">
                <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-amber-800">Tu membresía está por vencer</p>
                  <p className="text-sm text-amber-700 mt-1">
                    Renueva ahora para no perder acceso a tus beneficios.
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Renew Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <RefreshCw className="h-5 w-5 mr-2 text-emerald-600" />
              Renovar Membresía
            </CardTitle>
            <CardDescription>
              Mantente activo y continúa disfrutando de todos los beneficios UPIM
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="p-6 border rounded-xl">
                <h3 className="font-semibold text-lg mb-2">Renovación Anual</h3>
                <p className="text-gray-600 text-sm mb-4">Renueva por un año completo</p>
                <p className="text-3xl font-bold text-emerald-600 mb-4">
                  ${currentPlan.price.toLocaleString()} <span className="text-sm font-normal text-gray-500">MXN</span>
                </p>
                <Button className="w-full bg-emerald-600 hover:bg-emerald-700">
                  Renovar ahora
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
              
              <div className="p-6 border rounded-xl bg-gray-50">
                <h3 className="font-semibold text-lg mb-2">Beneficios incluidos</h3>
                <ul className="space-y-2">
                  {currentPlan.features.slice(0, 5).map((feature, index) => (
                    <li key={index} className="flex items-center space-x-2 text-sm">
                      <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                      <span className="text-gray-600">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Payment History */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <FileText className="h-5 w-5 mr-2 text-gray-600" />
              Historial de Pagos
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {mockPayments.map((payment) => (
                <div 
                  key={payment.id} 
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                >
                  <div className="flex items-center space-x-4">
                    <div className={`h-10 w-10 rounded-full flex items-center justify-center ${
                      payment.status === 'paid' ? 'bg-emerald-100' : 'bg-amber-100'
                    }`}>
                      {payment.status === 'paid' ? (
                        <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                      ) : (
                        <Clock className="h-5 w-5 text-amber-600" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{payment.concept}</p>
                      <p className="text-sm text-gray-500">
                        {new Date(payment.date).toLocaleDateString('es-MX', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric'
                        })}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-gray-900">
                      ${payment.amount.toLocaleString()} MXN
                    </p>
                    <Button variant="ghost" size="sm" className="text-emerald-600">
                      <Download className="h-4 w-4 mr-1" />
                      Recibo
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default MembresiaPage;
